import React, { useEffect, useState } from 'react';
import { cloneDeep, get, isEmpty, uniqueId } from 'lodash';
import { injectIntl, IntlShape } from 'react-intl';
import { Grid } from '@material-ui/core';
import { translate } from 'containers/services/translate';
import messages from 'containers/common/LegalProceedings/JudgementAdd/Recap/messages';
import {
  EMPTY_INCOME_CERTIFICATE,
  INCOME_CERTIFICATE,
  VICTIM,
} from 'containers/data-structure';
import { isMonitoringManager } from 'containers/AppContainer/utils';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import {
  formatIncomeCertificateVictims,
  getIncomeCertificateVictims,
  getVictimsWithoutIncomeCertificates,
} from 'containers/LawyerLegalProceedingsPage/judgementAdd/IncomeCertificate/utils';
import SahamAccordion from 'components/SahamAccordion';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { Victim } from 'containers/PreOpeningPage/typings';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import IncomeCertificatesForm from 'containers/LawyerLegalProceedingsPage/judgementAdd/IncomeCertificate/IncomeCertificatesForm';
import { StyledIncomeCertificateRecap } from 'containers/LawyerLegalProceedingsPage/judgementAdd/JudgementAddPronouncing/styled-components';
import IncomeCertificateRecap from 'containers/common/LegalProceedings/JudgementAdd/Recap/IncomeCertificatesRecap/IncomeCertificateRecap';
import IncomeCertificateDrawer from 'containers/common/LegalProceedings/JudgementAdd/Recap/IncomeCertificatesRecap/IncomeCertificateDrawer';
import ValidationDialog from 'containers/common/LegalProceedings/JudgementAdd/Recap/IncomeCertificatesRecap/ValidationDialog';
import { STATUS_VALIDATED } from 'containers/LawyerFeeNote/constants';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import { VictimType } from 'typings/globalTypes';
import { ClaimRecapType } from 'typings/claimTypes';
import { Listings } from 'containers/AppContainer/typings';

export interface IncomeCertificatesRecapProps {
  intl: IntlShape;
  victims: Victim[];
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  sagisVictimInfo: any;
  requestCreateIncomeCertificate?: Function;
  isManager: Boolean;
  setDocumentId: Function;
  documentId: any;
  claimId: number;
  claimNumber: string;
  judiciaryProcessId: any;
  readOnly?: boolean;
  requestUpdateIncomeCertificate?: Function;
  requestMakeDecisionIncomeCertificate?: Function;
  isConsultation: boolean;
  riskKey: string;
  claim: ClaimRecapType;
  judgementAdd: any;
  dahirAmount: [];
  requestGetDocByDocumentumId: Function;
  listings: Listings;
}

export const IncomeCertificatesRecap: React.FC<IncomeCertificatesRecapProps> = (
  props,
): JSX.Element => {
  const [expandIncomeCertificate, setExpandIncomeCertificate] = useState<
    boolean
  >(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState({} as any);
  const [openValidationDialog, setOpenValidationDialog] = useState(false);

  const [incomeCertificates, setIncomeCertificates] = useState({});
  const [selectedIncomeCertificate, setSelectedIncomeCertificate] = useState(
    {},
  );

  const handleChangeOnExpandIncomeCertificate = (
    event: React.ChangeEvent<{}>,
    expanded: boolean,
  ) => {
    event && event.stopPropagation();
    setExpandIncomeCertificate(expanded);
  };

  const [isActive, setisActive] = useState(false);

  const addNewIncomeCertificate = () => {
    setisActive(true);
    const incomeCertif = {
      ...incomeCertificates,
      [uniqueId()]: cloneDeep(EMPTY_INCOME_CERTIFICATE),
    };
    isActive && setIncomeCertificates(incomeCertif);
  };

  useEffect(() => {
    const newCertificates = getIncomeCertificateVictims(props.victims).reduce(
      (acc: any, victim: VictimType) => {
        if (victim.incomeCertificate) {
          acc[victim.id as any] = {
            ...victim.incomeCertificate,
            victim,
            income: get(victim, VICTIM.ANNUAL_INCOME),
          };
        }
        return acc;
      },
      {},
    );

    if (
      isEmpty(
        Object.values({
          ...incomeCertificates,
          ...newCertificates,
        }).filter(
          (el: any) =>
            isEmpty(get(el, 'victim', {})) ||
            get(el, INCOME_CERTIFICATE.VICTIM_ID, null) ===
              get(selectedIncomeCertificate, INCOME_CERTIFICATE.VICTIM_ID),
        ),
      )
    ) {
      setIncomeCertificates({ ...incomeCertificates, ...newCertificates });
    }
  }, [props.victims]);

  const handleIsDrawerOpen = (id: number) => (
    event?: React.ChangeEvent<{}>,
  ) => {
    event && event.stopPropagation();
    !!isDrawerOpen[id]
      ? setIsDrawerOpen({ ...isDrawerOpen, [id]: !isDrawerOpen[id] })
      : setIsDrawerOpen({ ...isDrawerOpen, [id]: true });
  };

  const handleCloseDialog = () => {
    setOpenValidationDialog(false);
  };

  const shouldDisplayEditButton = (incomeCertif: any) => {
    return (
      !props.isConsultation &&
      get(incomeCertif, 'status') != STATUS_VALIDATED &&
      (String(get(incomeCertif, 'lawyerCode')) ===
        currentIntervenantStorage?.get()?.sagis_code ||
        userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMANAGER)) &&
      !isMonitoringManager()
    );
  };
  return (
    <>
      {(!isEmpty(
        formatIncomeCertificateVictims(props.victims, incomeCertificates),
      ) ||
        !isEmpty(getVictimsWithoutIncomeCertificates(props.victims))) && (
        <StyledIncomeCertificateRecap>
          <Grid container direction="row" justify="space-between">
            <Grid item>
              <div className="pronounce-add-bloc-title">
                {translate(messages.judgementAddIncomeCertificateTitle, props)}
              </div>
            </Grid>
            <Grid item>
              {!isEmpty(getVictimsWithoutIncomeCertificates(props.victims)) &&
                !props.isConsultation &&
                !isMonitoringManager() && (
                  <div className="add-certificate">
                    <PrimaryButton
                      title={translate(
                        messages.addIncomeCertificateButtonTitle,
                        props,
                      )}
                      lefticon={<AddCircleOutlineIcon className="icon" />}
                      onClick={addNewIncomeCertificate}
                      className="primary-button"
                    />
                  </div>
                )}
            </Grid>
          </Grid>
          <section className="add-recap-bloc">
            {formatIncomeCertificateVictims(
              props.victims,
              incomeCertificates,
            ).map((incomeCertif: any, index: number) => (
              <>
                <SahamAccordion
                  accordionDetailsClassName="add-recap-bloc"
                  title={`${get(incomeCertif?.victim, [
                    VICTIM.FIRST_NAME,
                  ])} ${get(incomeCertif?.victim, [VICTIM.LAST_NAME])}`}
                  expanded={expandIncomeCertificate}
                  onChange={handleChangeOnExpandIncomeCertificate}
                  editButtonTitle={
                    shouldDisplayEditButton(incomeCertif)
                      ? translate(messages.editIncomeCertificate, props)
                      : undefined
                  }
                  editButtonOnClick={
                    shouldDisplayEditButton(incomeCertif)
                      ? handleIsDrawerOpen(
                          get(incomeCertif, INCOME_CERTIFICATE.VICTIM_ID),
                        )
                      : undefined
                  }
                >
                  <React.Fragment>
                    <IncomeCertificateRecap
                      key={index}
                      sagisVictimInfo={props.sagisVictimInfo}
                      uploadDocument={props.uploadDocument}
                      uploadedDocument={props.uploadedDocument}
                      resetUploadedDocument={props.resetUploadedDocument}
                      requestUpdateIncomeCertificate={
                        props.requestUpdateIncomeCertificate
                      }
                      isManager={props.isManager}
                      setDocumentId={props.setDocumentId}
                      documentId={props.documentId}
                      claimNumber={props.claimNumber}
                      claimId={props.claimId}
                      judiciaryProcessId={props.judiciaryProcessId}
                      readOnly={props.readOnly}
                      incomeCertificate={incomeCertif}
                      incomeCertificates={incomeCertificates}
                      setIncomeCertificates={setIncomeCertificates}
                      requestMakeDecisionIncomeCertificate={
                        props.requestMakeDecisionIncomeCertificate
                      }
                      isConsultation={props.isConsultation}
                      riskKey={props.riskKey}
                      claim={props.claim}
                      judgementAdd={props.judgementAdd}
                      dahirAmount={props.dahirAmount}
                      requestGetDocByDocumentumId={
                        props.requestGetDocByDocumentumId
                      }
                    />
                  </React.Fragment>
                </SahamAccordion>
                {!!isDrawerOpen &&
                  isDrawerOpen[
                    get(incomeCertif, INCOME_CERTIFICATE.VICTIM_ID)
                  ] && (
                    <IncomeCertificateDrawer
                      victim={get(incomeCertif, 'victim', {})}
                      uploadDocument={props.uploadDocument}
                      uploadedDocument={props.uploadedDocument}
                      resetUploadedDocument={props.resetUploadedDocument}
                      sagisVictimInfo={props.sagisVictimInfo}
                      isManager={props.isManager}
                      setDocumentId={props.setDocumentId}
                      documentId={props.documentId}
                      claimNumber={props.claimNumber}
                      claimId={props.claimId}
                      incomeCertificates={incomeCertificates}
                      setIncomeCertificates={setIncomeCertificates}
                      handleToggleDrawer={handleIsDrawerOpen(
                        get(incomeCertif, INCOME_CERTIFICATE.VICTIM_ID),
                      )}
                      handleOpenConfirmationDialog={() => {
                        setOpenValidationDialog(true);
                      }}
                      open={
                        isDrawerOpen[
                          get(incomeCertif, INCOME_CERTIFICATE.VICTIM_ID)
                        ]
                      }
                      setSelectedIncomeCertificate={
                        setSelectedIncomeCertificate
                      }
                    />
                  )}
                <ValidationDialog
                  sagisVictimInfo={props.sagisVictimInfo}
                  isManager={props.isManager}
                  claimNumber={props.claimNumber}
                  claimId={props.claimId}
                  judiciaryProcessId={props.judiciaryProcessId}
                  handleClose={handleCloseDialog}
                  incomeCertificate={selectedIncomeCertificate}
                  open={openValidationDialog}
                  requestUpdateIncomeCertificate={
                    props.requestUpdateIncomeCertificate
                  }
                  toUpdate={true}
                  riskKey={props.riskKey}
                  claim={props.claim}
                  judgementAdd={props.judgementAdd}
                  dahirAmount={props.dahirAmount}
                />
              </>
            ))}
            {isActive &&
              !isEmpty(getVictimsWithoutIncomeCertificates(props.victims)) && (
                <IncomeCertificatesForm
                  victims={getVictimsWithoutIncomeCertificates(props.victims)}
                  uploadDocument={props.uploadDocument}
                  uploadedDocument={props.uploadedDocument}
                  resetUploadedDocument={props.resetUploadedDocument}
                  sagisVictimInfo={props.sagisVictimInfo}
                  requestCreateIncomeCertificate={
                    props.requestCreateIncomeCertificate
                  }
                  isManager={props.isManager}
                  setDocumentId={props.setDocumentId}
                  documentId={props.documentId}
                  claimNumber={props.claimNumber}
                  claimId={props.claimId}
                  incomeCertificates={incomeCertificates}
                  setIncomeCertificates={setIncomeCertificates}
                  judiciaryProcessId={props.judiciaryProcessId}
                  dahirAmount={props.dahirAmount}
                  riskKey={props.riskKey}
                  claim={props.claim}
                  setIsIncomeCertificateFormOn={setisActive}
                  listings={props.listings}
                />
              )}
          </section>
        </StyledIncomeCertificateRecap>
      )}
    </>
  );
};

export default injectIntl(IncomeCertificatesRecap);
